import { useEffect, useRef, useState } from 'react'
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist'
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist'
import styles from './PdfViewer.module.css'

function workerSrc() {
  return `${window.location.origin}${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`
}

GlobalWorkerOptions.workerSrc = typeof window !== 'undefined' ? workerSrc() : ''

type PdfPagesProps = {
  blob: Blob
  zoom: number
}

function PdfPages({ blob, zoom }: PdfPagesProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const canvasRefs = useRef<Array<HTMLCanvasElement | null>>([])
  const pdfRef = useRef<PDFDocumentProxy | null>(null)
  const [layoutWidth, setLayoutWidth] = useState(0)
  const [numPages, setNumPages] = useState(0)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')

  useEffect(() => {
    GlobalWorkerOptions.workerSrc = workerSrc()
  }, [])

  // Ширина «вписанной» страницы = ширина скролл-контейнера, не растёт от zoom
  useEffect(() => {
    const el = hostRef.current?.parentElement
    if (!el) return

    const update = () => {
      const next = Math.floor(el.clientWidth - 24)
      if (next > 0) {
        setLayoutWidth(next)
      }
    }

    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    let cancelled = false
    setStatus('loading')
    setNumPages(0)
    canvasRefs.current = []
    pdfRef.current?.destroy()
    pdfRef.current = null

    void (async () => {
      try {
        const data = new Uint8Array(await blob.arrayBuffer())
        const pdf = await getDocument({ data }).promise
        if (cancelled) {
          pdf.destroy()
          return
        }
        pdfRef.current = pdf
        setNumPages(pdf.numPages)
        setStatus('ready')
      } catch {
        if (!cancelled) setStatus('error')
      }
    })()

    return () => {
      cancelled = true
      pdfRef.current?.destroy()
      pdfRef.current = null
    }
  }, [blob])

  useEffect(() => {
    const pdf = pdfRef.current
    if (!pdf || status !== 'ready' || !layoutWidth || numPages === 0) return

    let cancelled = false
    const tasks: RenderTask[] = []

    void (async () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      for (let pageNumber = 1; pageNumber <= numPages; pageNumber += 1) {
        if (cancelled) return
        const canvas = canvasRefs.current[pageNumber - 1]
        if (!canvas) continue

        const page = await pdf.getPage(pageNumber)
        const base = page.getViewport({ scale: 1 })
        const cssWidth = layoutWidth * zoom
        const scale = cssWidth / base.width
        const viewport = page.getViewport({ scale: scale * dpr })
        const context = canvas.getContext('2d')
        if (!context) continue

        canvas.width = viewport.width
        canvas.height = viewport.height
        canvas.style.width = `${cssWidth}px`
        canvas.style.height = `${base.height * scale}px`

        const task = page.render({ canvasContext: context, viewport })
        tasks.push(task)
        try {
          await task.promise
        } catch {
          // cancelled
        }
      }
    })()

    return () => {
      cancelled = true
      tasks.forEach((task) => task.cancel())
    }
  }, [numPages, status, layoutWidth, zoom])

  return (
    <div ref={hostRef} className={styles.pages}>
      {status === 'loading' && <div className={styles.message}>Загрузка…</div>}
      {status === 'error' && (
        <div className={styles.message}>Не удалось показать документ</div>
      )}
      {status === 'ready' &&
        Array.from({ length: numPages }, (_, index) => (
          <canvas
            key={index}
            ref={(node) => {
              canvasRefs.current[index] = node
            }}
            className={styles.page}
          />
        ))}
    </div>
  )
}

export default PdfPages
